import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { useUnifiedSearch } from '@/shared/api/unifiedSearchQueries'
import { isAllowedCommandPalettePath } from '@/shared/lib/safeRedirect'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'
import { useOptionalModuleNavFlags } from '@/shared/hooks/useOptionalModuleNavFlags'
import { isOptionalModulePathVisible } from '@/shared/lib/optionalModuleNav'
import { cn } from '@/shared/lib/utils'
import type { UnifiedSearchHit } from '@/shared/schemas/unifiedSearch.schema'

function shortcutHint(): string {
  if (typeof navigator === 'undefined') return 'Ctrl K'

  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? '⌘K' : 'Ctrl K'
}

function normalizeSearchTerm(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

interface NavCommandItem {
  kind: 'nav'
  to: string
  labelKey: string
}

interface HitCommandItem {
  kind: 'hit'
  id: string
  to: string
  label: string
  type: UnifiedSearchHit['type']
}

type PaletteItem = NavCommandItem | HitCommandItem

const COMMAND_ITEMS: NavCommandItem[] = [
  { kind: 'nav', to: '/', labelKey: 'app.layout.nav_home' },
  { kind: 'nav', to: '/reservation/properties', labelKey: 'app.layout.nav_properties' },
  { kind: 'nav', to: '/ticketing', labelKey: 'app.layout.nav_ticketing' },
  { kind: 'nav', to: '/lottery', labelKey: 'app.layout.nav_lottery' },
  { kind: 'nav', to: '/match-event', labelKey: 'app.layout.nav_match_event' },
  { kind: 'nav', to: '/group-trips', labelKey: 'app.layout.nav_group_trips' },
  { kind: 'nav', to: '/listings', labelKey: 'app.layout.nav_listings' },
  { kind: 'nav', to: '/partner-offers', labelKey: 'app.layout.nav_partner_offers' },
  { kind: 'nav', to: '/colony', labelKey: 'app.layout.nav_colony' },
  { kind: 'nav', to: '/reservation/high-season', labelKey: 'app.layout.nav_high_season' },
  { kind: 'nav', to: '/notifications', labelKey: 'app.layout.nav_notifications' },
  { kind: 'nav', to: '/me', labelKey: 'app.layout.nav_profile' },
  { kind: 'nav', to: '/me/reservations', labelKey: 'app.layout.nav_reservations' },
  { kind: 'nav', to: '/faq', labelKey: 'faq.title' },
]

function hitTypeLabelKey(type: UnifiedSearchHit['type']): string {
  switch (type) {
    case 'property':
      return 'command.types.property'
    case 'ticketing_offer':
      return 'command.types.ticketing_offer'
    case 'lottery_draw':
      return 'command.types.lottery_draw'
    case 'match_event':
      return 'command.types.match_event'
    case 'group_trip':
      return 'command.types.group_trip'
    case 'partner_offer':
      return 'command.types.partner_offer'
    case 'listing':
      return 'command.types.listing'
    case 'article':
      return 'command.types.article'
    case 'page':
      return 'command.types.page'
  }
}

function paletteGroupKey(item: PaletteItem): string {
  if (item.kind === 'hit') {
    return item.type
  }
  if (item.to.startsWith('/reservation')) return 'property'
  if (item.to.startsWith('/ticketing')) return 'ticketing_offer'
  if (item.to.startsWith('/lottery')) return 'lottery_draw'
  if (item.to.startsWith('/match-event')) return 'match_event'
  if (item.to.startsWith('/group-trips')) return 'group_trip'
  if (item.to.startsWith('/partner-offers')) return 'partner_offer'
  if (item.to.startsWith('/listings')) return 'listing'
  if (item.to.startsWith('/blog')) return 'article'
  if (item.to.startsWith('/me') || item.to.startsWith('/notifications')) return 'account'

  return 'page'
}

/**
 * Cross-platform command palette backed by unified search with a 300 ms debounce.
 */
export function CommandPalette() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [shortcut] = useState(shortcutHint)
  const debouncedQuery = useDebouncedValue(query, 300)

  const searchQuery = useUnifiedSearch(debouncedQuery, open)
  const moduleFlags = useOptionalModuleNavFlags()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const results = useMemo((): PaletteItem[] => {
    const normalized = normalizeSearchTerm(query.trim())
    const nav: PaletteItem[] = COMMAND_ITEMS.filter(
      (item) =>
        isOptionalModulePathVisible(item.to, moduleFlags) &&
        (normalized === '' || normalizeSearchTerm(t(item.labelKey)).includes(normalized)),
    )

    const hits: PaletteItem[] = (searchQuery.data ?? [])
      .filter((hit) => isAllowedCommandPalettePath(hit.url))
      .map((hit) => ({
        kind: 'hit' as const,
        id: hit.id,
        to: hit.url,
        label: hit.title,
        type: hit.type,
      }))

    if (normalized === '') {
      return nav
    }

    return [...hits, ...nav]
  }, [moduleFlags, query, searchQuery.data, t])

  const groupedResults = useMemo(() => {
    const groups: Array<{ key: string; items: Array<{ item: PaletteItem; index: number }> }> = []
    const byKey = new Map<string, (typeof groups)[number]>()

    results.forEach((item, index) => {
      const key = paletteGroupKey(item)
      let group = byKey.get(key)
      if (group === undefined) {
        group = { key, items: [] }
        byKey.set(key, group)
        groups.push(group)
      }
      group.items.push({ item, index })
    })

    return groups
  }, [results])

  const onOpenChange = (next: boolean): void => {
    setOpen(next)
    if (next) {
      setQuery('')
      setActive(0)
    }
  }

  const go = (to: string, kind: PaletteItem['kind']): void => {
    if (kind === 'hit' && !isAllowedCommandPalettePath(to)) {
      return
    }
    setOpen(false)
    void navigate({ to })
  }

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (results.length === 0) {
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => (index + 1) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const target = results[Math.min(active, results.length - 1)]
      if (target) {
        go(target.to, target.kind)
      }
    }
  }

  const activeId = results.length > 0 ? `command-option-${Math.min(active, results.length - 1)}` : undefined
  const showSearching =
    open && query.trim().length > 0 && (searchQuery.isFetching || query !== debouncedQuery)

  return (
    <>
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-label={t('command.trigger')}
        aria-keyshortcuts="Meta+K Control+K"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Search className="size-4" aria-hidden />
        <span className="hidden sm:inline">{t('command.trigger')}</span>
        <kbd className="ml-1 hidden rounded border border-border bg-muted px-1.5 font-mono text-[11px] sm:inline" aria-hidden>
          {shortcut}
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent showCloseButton={false} className="top-[15%] translate-y-0 gap-0 p-0 sm:max-w-xl">
          <DialogTitle className="sr-only">{t('command.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('command.description')}</DialogDescription>
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <input
              type="text"
              role="combobox"
              aria-expanded
              aria-controls="command-listbox"
              aria-activedescendant={activeId}
              aria-label={t('command.placeholder')}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- Initial focus is expected in this modal palette.
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              onKeyDown={onInputKeyDown}
              placeholder={t('command.placeholder')}
              className="w-full bg-transparent py-4 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          {showSearching ? (
            <p className="px-4 py-3 text-sm text-muted-foreground" role="status">
              {t('command.searching')}
            </p>
          ) : null}
          {results.length === 0 && !showSearching ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('command.empty')}</p>
          ) : null}
          {results.length > 0 ? (
            <div id="command-listbox" role="listbox" aria-label={t('command.title')} className="max-h-80 overflow-y-auto p-2">
              {groupedResults.map((group) => (
                <section
                  key={group.key}
                  role="group"
                  aria-labelledby={`command-group-${group.key}`}
                  className="[&:not(:first-child)]:mt-3"
                >
                  <h3
                    id={`command-group-${group.key}`}
                    className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {t(`command.groups.${group.key}`)}
                  </h3>
                  {group.items.map(({ item, index }) => {
                    const isActive = index === Math.min(active, results.length - 1)
                    const label = item.kind === 'nav' ? t(item.labelKey) : item.label
                    return (
                      <button
                        key={item.kind === 'nav' ? `nav-${item.to}` : `hit-${item.type}-${item.id}`}
                        id={`command-option-${index}`}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => go(item.to, item.kind)}
                        onMouseEnter={() => setActive(index)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground',
                          isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                        )}
                      >
                        <span className="min-w-0 truncate">{label}</span>
                        {item.kind === 'hit' ? (
                          <span className="shrink-0 text-xs text-muted-foreground">{t(hitTypeLabelKey(item.type))}</span>
                        ) : null}
                      </button>
                    )
                  })}
                </section>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
